import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import moment from "moment";
import { styles } from "../../utils/styles";
import { acquirerList } from "../../utils/constants";
import { COLORS } from "../../utils/colors";
import { LoadingButton } from "@mui/lab";

const ViewDetailDialog = ({
  open,
  onClose,
  record,
  loaded,
  rejectLoading,
  approveLoading,
  deleteLoading,
  canEdit,
  canDelete,
  canApproveReject,
  onEdit,
  onRequestDelete,
  onApprove,
  onReject,
}) => {
  if (!open) return null;

  const isPending = record?.status === "Pending";
  const isApproved = record?.status === "Approved";
  const isRejected = record?.status === "Rejected";
  const showApproveReject = isPending && canApproveReject;
  const showEdit = canEdit && !showApproveReject && !isApproved && !isRejected;
  const showDelete = canDelete && !showApproveReject && !isApproved && !isRejected;

  const getStatusColor = (status) => {
    if (status === "Pending") return "#ff9800";
    if (status === "Approved") return "#4caf50";
    if (status === "Rejected") return "#f44336";
    return "inherit";
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ px: 2, py: 1 }}>
        <Box sx={styles.flexBetween}>
          No Payout Day Details
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loaded && record ? (
          <Grid container spacing={2} sx={{ pt: 0 }}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Date
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body1">
                {record.no_payout_date
                  ? moment(record.no_payout_date).format("YYYY-MM-DD")
                  : ""}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Day
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body1">{record.no_payout_day || ""}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Acquirer
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body1">
                {record.acquirer && record.acquirer !== "all"
                  ? (acquirerList.find((o) => o.id === record.acquirer)?.name || record.acquirer)
                  : "All"}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Description
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body1">{record.description || ""}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: getStatusColor(record.status) }}
              >
                {record.status || ""}
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ py: 2, textAlign: "center" }}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        {showApproveReject && (
          <>
            <LoadingButton
              variant="outlined"
              onClick={() => onReject(record?.id)}
              loading={rejectLoading}
              disabled={approveLoading || rejectLoading}
              sx={{
                mr: 1
              }}
            >
              Reject
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={() => onApprove(record?.id)}
              loading={approveLoading}
              disabled={approveLoading || rejectLoading}
              sx={{ 
                backgroundColor: COLORS.OXYGEN7_PURPLE,
                '&:hover': {
                  backgroundColor: 'rgba(91, 45, 130, 0.8)'
                },
                mr: 1
              }}
            >
              Approve
            </LoadingButton>
          </>
        )}
        {showEdit && (
          <Button
            variant="contained"
            onClick={() => onEdit(record)}
            sx={{ ...styles.customButton, mr: 1 }}
          >
            Edit
          </Button>
        )}
        {showDelete && (
          <LoadingButton
            variant="outlined"
            onClick={() => onRequestDelete(record?.id)}
            loading={deleteLoading}
            disabled={approveLoading || rejectLoading || deleteLoading}
            sx={{ mr: 1 }}
          >
            Delete
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ViewDetailDialog;
